import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';

import { AngularContext } from 'react-app';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { config } from '@config/pendo.config.json';
import { largeSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import ReflectionStats from './reflection-stats';
import ReflectionLookingBack from './reflection-looking-back';
import ReflectionContent from './reflection-content';

const staticStyles = css`
  &.modal-page {
    background: url('${prodPathReplace('images/reflections-bg.svg')}') no-repeat
        top center,
      rgba(255, 255, 255, 0.8);
    background-size: 100% auto, auto;
  }

  .scrolling-shape {
    top: 0;
    left: 0;
    right: 0;
    background: url('${prodPathReplace(
        'images/reflections-bg-shape-scroll.svg'
      )}')
      no-repeat top center;
    background-size: 100% auto;
    pointer-events: none;
    z-index: 0;
  }

  .reflection-content {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 1;

    .reflection-content-header {
      padding: ${tripleSpacing}px 125px ${largeSpacing}px;
      gap: ${largeSpacing}px;
    }
  }
`;

const scrollingShapeStyle = (scrollPosition: number) => css`
  .scrolling-shape {
    transform: translateY(-${scrollPosition}px);
  }
`;

type ReflectionWorkspaceContainerProps = {
  programId: number;
  connectionId: number;
  programName: string;
};

const ReflectionWorkspaceContainer = ({
  programId,
  connectionId,
  programName,
}: ReflectionWorkspaceContainerProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);

  const [scrollPosition, setScrollPosition] = useState(0);
  const rafRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.programReflections
      .modal;

  const resetReflectionsParam = () => {
    const params = { showReflections: undefined };
    $state.go($state.current, params, { notify: false });
  };

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const handleScroll = () => {
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        setScrollPosition(contentEl.scrollTop);
        rafRef.current = null;
      });
    };

    contentEl.addEventListener('scroll', handleScroll);
    return () => {
      contentEl.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <NvModal
      show
      doubleModal
      fullHeight={false}
      type={ModalType.NO_DIALOG}
      closeDataQa={dataQATag.close}
      onClose={resetReflectionsParam}
      closeOnBackdropClick
      body={
        <div
          css={[staticStyles, scrollingShapeStyle(scrollPosition)]}
          className='modal-page overflow-hidden bg-white'
        >
          <div className='scrolling-shape position-absolute h-100' />
          <div
            ref={contentRef}
            className='reflection-content position-absolute'
          >
            <div className='reflection-content-header d-flex flex-column text-center'>
              <ReflectionStats
                programId={programId}
                connectionId={connectionId}
                programName={programName}
              />
              <ReflectionLookingBack connectionId={connectionId} />
              <ReflectionContent
                programId={programId}
                connectionId={connectionId}
              />
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ReflectionWorkspaceContainer;
